import React, { useCallback, useState, useRef, memo, useEffect } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import isEqual from 'lodash.isequal';
import debounce from 'lodash.debounce';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';

// Components
import { MainButton } from '../../../inputs/buttons/MainButton';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import SearchableDropdown from '../../../inputs/SearchableDropdown';
import InputText from '../../../inputs/InputText';
import { ArrowRadioGroup } from '../../../../pages/main/comunications/NewComunication/styles';
import { MapComponent } from '../../../display/Map';

// Ubicación puntual: coordenadas exactas del PK desde la malla precacheada.
import { coordsFromPk } from '../../../../utils/coordsFromPk';
import { locationMapGeometry } from '../../../../utils/locationMapGeometry';
import { getCachedRoadMesh } from '../../../../offline/roadMeshStore';
import { pkEquals } from '../../../../utils/pkCompare';

// Styles
import {
  BlackButton,
  GreenButton,
  MainContainer,
  MapContainer,
  RoadInputsGroup,
  RowStyled,
} from './styles';
import {
  ContainerError,
  GeneralContainer,
  ButtonContainer,
  RowsContainer,
  Title,
} from '../styles';

// Types
import {
  type LocationTypes,
  ActionDirection,
  ActionMargin,
  type LocationFormValues,
  Coordinates,
  Concessions,
  GroupTypes,
} from './types';

import { type PartialDirection } from '../../../inputs/buttons/ArrowDirectionButton/types';
import { type WorkOrder } from '../../../../types/workOrder';
import { type OptionSelected } from '../../../inputs/DropDown/DropDownList/types';
import { ImperativeRefInput } from '../../../inputs/DropDown/DropDownInput/types';

// Utils
import { useCacheFormInSessionStorage } from '../../../../hooks/useCacheForm';
import { getLatLon } from '../../../../utils/location';
import { pkFormatter, PK_FORMAT_REGEX } from '../../../../utils/transform';
import { useAuth } from '../../../../hooks/useAuth';
import { useAppConfig } from '../../../../hooks/useAppConfig';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { compressSessionStorage } from '../../../../utils/sessionStorage';

//  GQL
import { client } from '../../../../gql/client';
import {
  GET_CONCESSION_BY_ID,
  GET_ROAD_BY_ID,
  GET_ROADS_BY_IDS,
} from '../../../../gql/queries/geoPositions/roads';
import { GET_POSITION_ROADS } from '../../../../gql/queries/geoPositions/geoPositionRoads';

// Schema Yups
import { createLocationSchema } from './schema';
import { Typography } from '../../../display/Typography';
import { UNSPECIFIC } from '../../../../types/utils';
import { pkValidation } from '../../../../utils/pkValidation';
import { fetchDefaultData } from '../../../../utils/fetchData';
import { OrderState } from '../../../../types/orders';
import { registerPk } from '../../../../utils/pkField';
import { useAutolocatePk } from '../../../../hooks/useAutolocatePk';
import { requestPositionOnce } from '../../../../utils/requestPositionOnce';
import { showErrorToast } from '../../../../utils/toasts';
import type { PkCandidate } from '../../../../utils/resolvePkAtPosition';
import {
  translateLaneOptions,
  translateTrackOptions,
  type CatalogEntry,
} from '../../../../utils/catalogLabels';
import { drawingWindow } from '../../../../utils/drawingWindow';

const MemorizedMap = memo(MapComponent, isEqual);

const LocationForm = ({
  onFormSubmit,
  namingPersistForm,
  initialData,
  textButton = 'Continuar',
}: LocationTypes) => {
  const { t } = useTranslation('forms');
  const { availableConcessionIds, loading: appConfigLoading } = useAppConfig();
  // Feature flag para mostrar/ocultar campo margen
  const showMargin = useFeatureFlag('showMargin', true);
  // Si hay exactamente 1 concesión, es la "por defecto"
  const defaultConcessionId =
    availableConcessionIds.length === 1 ? availableConcessionIds[0] : null;
  let pksAndData:
    | {
      pkInit: string;
      pkEnd: string;
      direction: string;
      road: string;
    }
    | undefined;
  const directionRequired = useFeatureFlag('directionRequired', true);
  const [mapError, setMapError] = useState<string | null>(null);
  const [centerCoordinates, setCenterCoordinates] =
    useState<Coordinates | null>(null);
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const [path, setPath] = useState<Coordinates[] | []>([]);
  // Cuando la actuación está en un PUNTO (pkInit === pkEnd) se pinta un
  // marcador en vez de la polilínea. Manda sobre `path`.
  const [point, setPoint] = useState<Coordinates | null>(null);
  const [displayMap, setDisplayMap] = useState(false);
  /**
   * El GPS tarda, y sin esto el botón parece muerto mientras tanto.
   *
   * 🔴 No es cosmética: el hook tiene un guard antidoble-pulsación
   * (`enCurso`), así que con el botón habilitado el operario repulsa y esas
   * pulsaciones se las traga en silencio. Entre este estado y
   * `autolocate.isLoading` el botón se deshabilita de punta a punta.
   */
  const [isLocating, setIsLocating] = useState(false);

  // INTEGRACIÓN 9: Detectar validación diferida desde Resumen
  const locationState = useLocation();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    trigger, // ✅ Necesario para validación manual
    formState: { errors },
  } = useForm<LocationFormValues & WorkOrder>({
    defaultValues: {
      actionCity:
        ((initialData?.concessions as Concessions)?.find(
          (concession) => concession?.id === initialData?.concession
        )?.location as string) ?? '',
      actionPkInitRoad: initialData?.pkInitRoad ?? '',
      actionPkEndRoad: initialData?.pkEndRoad ?? '',
      actionPkInit: initialData?.pkInit ?? '',
      actionPkEnd: initialData?.pkEnd ?? '',
      actionConcession: initialData?.concession ?? '',
      actionTrack: initialData?.actionTrack
        ? initialData.actionTrack
        : initialData?.track,
      actionLane: initialData?.actionLane
        ? initialData.actionLane
        : initialData?.lane,
      actionRoad: initialData?.actionRoad
        ? initialData?.actionRoad
        : initialData?.road,
      actionDirection: initialData?.actionDirection
        ? initialData?.actionDirection
        : initialData?.direction,
      actionMargin: initialData?.actionMargin
        ? initialData?.actionMargin
        : initialData?.margin,
      ...initialData,
    },
    resolver: yupResolver(createLocationSchema(directionRequired, showMargin)) as any,
  });

  // INTEGRACIÓN 9: Ejecutar validación al montar si viene del resumen con errores
  useEffect(() => {
    if (locationState.state?.validateOnMount) {
      console.log('🚨 LocationForm: Triggering validation on mount');
      trigger();
    }
  }, []);

  // INTEGRACIÓN 9: Función para guardar draft y navegar SIN validar
  const handleSaveDraft = () => {
    const values = getValues();
    // Guardar en session storage (para persistencia)
    const compressed = compressSessionStorage(JSON.stringify(values));
    sessionStorage.setItem(namingPersistForm, compressed);

    // Llamar al submit handler del padre (que solo navega)
    onFormSubmit(values);
  };

  const roadDropdownInputRef = useRef<ImperativeRefInput | null>(null);

  useCacheFormInSessionStorage(namingPersistForm, control);

  // Watchers
  const roads = useWatch({
    control,
    name: 'roads',
  });

  const actionPkInit = useWatch({
    control,
    name: 'actionPkInit',
  });

  const actionPkEnd = useWatch({
    control,
    name: 'actionPkEnd',
  });

  const actionPkInitRoad = useWatch({
    control,
    name: 'actionPkInitRoad',
  });

  const actionPkEndRoad = useWatch({
    control,
    name: 'actionPkEndRoad',
  });

  const actionDirection = useWatch({
    control,
    name: 'actionDirection',
  });

  const actionRoad = useWatch({
    control,
    name: 'actionRoad',
  });

  const actionCoordinates = useWatch({
    control,
    name: 'actionCoordinates',
  });

  const actionRoadDirections = useWatch({
    control,
    name: 'actionRoadDirections',
  });

  // Cargar roadDirections cuando hay initialData con road preseleccionada (edición)
  useEffect(() => {
    const preselectedRoadId = initialData?.actionRoad || initialData?.road;
    if (preselectedRoadId && !actionRoadDirections) {
      const loadRoadDirections = async () => {
        try {
          const {
            data: { road },
          } = await client.query({
            query: GET_ROAD_BY_ID,
            variables: { id: preselectedRoadId },
          });
          setValue('actionRoadDirections', road.directions);
        } catch (err) {
          Sentry.captureException(err);
          console.log('Error loading road directions:', err);
        }
      };
      loadRoadDirections();
    }
  }, [initialData, actionRoadDirections, setValue]);

  /** Vuelca en el estado lo que `locationMapGeometry` haya decidido pintar. */
  const paint = useCallback(
    (geometry: {
      center: Coordinates | null;
      point: Coordinates | null;
      path: Coordinates[];
    }) => {
      if (geometry.center) setCenterCoordinates(geometry.center);
      setPoint(geometry.point);
      setPath(geometry.path);
    },
    []
  );

  const checkGeoPositionRoad = useCallback(
    debounce(
      ({
        pkInit,
        pkEnd,
        direction,
        road,
      }: {
        pkInit: string | undefined;
        pkEnd: string | undefined;
        direction: string | undefined;
        road: string | undefined;
      }) => {
        if (
          !!pkInit &&
          !!pkEnd &&
          !!direction &&
          direction !== UNSPECIFIC &&
          !!road
        ) {
          const tasks = async () => {
            const pkInitRaw = pkInit?.replace(/ /g, '').split('+');
            const pkEndRaw = pkEnd?.replace(/ /g, '').split('+');
            // Ventana puntual/estrecha (pkInit ≈ pkEnd, p.ej. actuación en un
            // punto): la malla de vértices del trazado (~100 m) puede no tener
            // vértice en ese PK exacto para el sentido pedido — las mallas de
            // Girona y Lleida NO coinciden (cazado 2026-07-21: 82+800 puntual
            // pintaba en Girona y no en Lleida) → 0 puntos y el mapa no salía.
            // Ampliamos ±300 m SOLO para dibujar; los PKs del form no se tocan.
            // La ventana de dibujo vive en `drawingWindow`, con tests: se
            // escribió a la vez que el arreglo del kilómetro largo y sin
            // cobertura, que es justo lo que no debe repetirse.
            const pkInicial = {
              km: Number(pkInitRaw[0]),
              meter: Number(pkInitRaw[1]),
            };
            const pkFinal = {
              km: Number(pkEndRaw[0]),
              meter: Number(pkEndRaw[1]),
            };
            // La actuación está en un PUNTO, no en un trecho. Qué se pinta
            // en cada caso lo decide `locationMapGeometry`, que es la misma
            // función que usa el resumen.
            const isPunctual = pkEquals(pkInicial, pkFinal);
            // La malla precacheada da la coordenada EXACTA del PK. Con ella no
            // hace falta ni consultar el trazado — y así el punto sale también
            // sin cobertura.
            const meshPoint = isPunctual
              ? coordsFromPk(pkInicial, getCachedRoadMesh(road))
              : null;
            if (meshPoint) {
              paint(locationMapGeometry({ isPunctual, meshPoint, vertices: [] }));
              return;
            }
            const ventana = drawingWindow(pkInicial, pkFinal);
            const queryGeoPositions = (dir: string) =>
              client.query({
                query: GET_POSITION_ROADS,
                variables: {
                  input: {
                    pkInitKm: ventana.lo.km,
                    pkInitMeter: ventana.lo.meter,
                    pkEndKm: ventana.hi.km,
                    pkEndMeter: ventana.hi.meter,
                    roadId: road,
                    direction: dir,
                  },
                },
              });
            const requestedDirection = direction === 'both' ? 'top' : direction;
            let {
              data: { geoPositionRoads },
            } = await queryGeoPositions(requestedDirection);
            // Fallback de sentido (espejo del both→top de siempre): si el
            // sentido pedido no tiene puntos en la ventana (datasets por
            // sentido desiguales), pintamos el trazado del contrario — a zoom
            // de mapa son la misma línea.
            if (geoPositionRoads.length === 0) {
              const fallbackDirection =
                requestedDirection === 'top' ? 'down' : 'top';
              ({
                data: { geoPositionRoads },
              } = await queryGeoPositions(fallbackDirection));
            }
            // Se pinta SIEMPRE, también con cero vértices: antes, si el servidor
            // no devolvía nada, no se llamaba a `paint` y el mapa se quedaba
            // con el punto o la línea del PK ANTERIOR — cambiar el PK final de
            // 82+800 a 82+900 seguía enseñando el punto del 82+800, y al bajar
            // desde uno que no pintaba se quedaba en blanco. Un mapa que no
            // sabe qué pintar tiene que quedarse vacío, no mentir con lo viejo.
            const vertices = geoPositionRoads.map(
              (position: { latitude: number; longitude: number }) => ({
                lat: position.latitude,
                lng: position.longitude,
              })
            );
            paint(locationMapGeometry({ isPunctual, meshPoint, vertices }));
          };
          tasks().catch((err) => {
            Sentry.captureException(err);
            console.log(err);
          });
        }
      },
      1000
    ),
    []
  );

  useEffect(() => {
    const invalidRoads = (roads || []).filter(
      (road: any) =>
        road.name === 'Fora Concessió' || road.name === 'Sala de Control'
    );

    const pkParents = {
      pkInitRoad: actionPkInitRoad,
      pkEndRoad: actionPkEndRoad,
    };

    if (
      !invalidRoads.map((road) => road.id).includes(actionRoad) &&
      actionRoad &&
      actionPkInit &&
      actionPkEnd &&
      actionDirection &&
      actionDirection !== UNSPECIFIC &&
      actionPkInit.match(PK_FORMAT_REGEX) &&
      actionPkEnd.match(PK_FORMAT_REGEX) &&
      pkValidation(actionPkInit, pkParents) &&
      pkValidation(actionPkEnd, pkParents)
    ) {
      pksAndData =
        !!actionPkInit &&
          !!actionPkEnd &&
          !!actionDirection &&
          actionDirection !== UNSPECIFIC &&
          !!actionRoad
          ? {
            pkInit: actionPkInit,
            pkEnd: actionPkEnd,
            direction: actionDirection,
            road: actionRoad,
          }
          : !!initialData?.pkInit &&
            !!initialData?.pkEnd &&
            !!initialData?.direction &&
            initialData?.direction !== UNSPECIFIC &&
            !!initialData?.road
            ? {
              pkInit: initialData?.pkInit,
              pkEnd: initialData?.pkEnd,
              direction: initialData?.direction,
              road: initialData?.road,
            }
            : {
              pkInit: '61+100',
              pkEnd: '69+100',
              direction: 'down',
              road: '6463453c0f1a3c516162ef3f',
            };
      checkGeoPositionRoad(pksAndData);
      setDisplayMap(true);
      setMapError('');
    } else {
      setDisplayMap(false);
      setMapError(
        'El mapa només es pintarà si el pk inicial, el pk final, la carretera i el sentit són correctes o si geolocalitzes la teva posició'
      );
    }
  }, [actionRoad, actionDirection, actionPkInit, actionPkEnd]);

  const onSelectConcession = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        const {
          data: { concession },
        } = await client.query({
          query: GET_CONCESSION_BY_ID,
          variables: { id: option?.value },
        });

        setCenterCoordinates(
          getLatLon(concession.id, initialData?.concessions)
        );

        setValue('actionCity', concession.location);

        const { readRoads } = client.readQuery({
          query: GET_ROADS_BY_IDS,
          variables: { ids: concession.roadIDs ?? [] },
        });
        if (readRoads.length === 0) {
          await fetchDefaultData();
          const { readRoads } = client.readQuery({
            query: GET_ROADS_BY_IDS,
            variables: { ids: concession.roadIDs ?? [] },
          });
          setValue('roads', readRoads);
        } else {
          setValue('roads', readRoads);
        }

        roadDropdownInputRef.current?.clearSelected();
        setValue('actionRoad', '');

        onChange(option?.value ?? '');
      };

      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    []
  );

  const onSelectRoad = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        const {
          data: { road },
        } = await client.query({
          query: GET_ROAD_BY_ID,
          variables: { id: option?.value },
        });

        setValue(
          'actionPkInitRoad',
          pkFormatter(road.pkInitKm, road.pkInitMeter, true)
        );
        setValue(
          'actionPkEndRoad',
          pkFormatter(road.pkEndKm, road.pkEndMeter, true)
        );
        setValue('actionRoadDirections', road.directions);

        onChange(option?.value ?? '');
      };
      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    []
  );

  // Pre-select tenant's default concession from AppConfig
  useEffect(() => {
    // Esperar a que appConfig termine de cargar
    if (appConfigLoading) return;

    // Si hay defaultConcessionId del tenant (puede ser null para algunos tenants)
    if (
      defaultConcessionId &&
      control._defaultValues?.concessions &&
      !initialData
    ) {
      const tenantConcession = control._defaultValues.concessions.find(
        (c: any) => c.id === defaultConcessionId
      );

      if (
        tenantConcession &&
        tenantConcession.name !== 'CX' &&
        tenantConcession.name &&
        tenantConcession.id
      ) {
        const concessionOption = {
          label: tenantConcession.name,
          value: tenantConcession.id,
        };

        setValue('actionConcession', concessionOption.value);

        // Trigger onSelectConcession to load roads, etc.
        onSelectConcession(concessionOption, (value) =>
          setValue('actionConcession', value)
        );
      }
    }
    // Si NO hay defaultConcessionId, dejar el dropdown vacío para que el usuario seleccione
  }, [
    defaultConcessionId,
    appConfigLoading,
    control._defaultValues,
    initialData,
    setValue,
    onSelectConcession,
  ]);

  /**
   * Además del marcador, el PK.
   *
   * El botón de la diana ya pedía la posición para centrar el mapa; teniendo el
   * aparato la posición, que el operario teclee el PK a mano era el desperdicio
   * que motiva toda la feature. Aquí los campos se llaman `actionPk*`, de ahí
   * que la escritura viva en el formulario y no en el hook.
   */
  const autolocate = useAutolocatePk({
    getPreferredRoadId: () => getValues('actionRoad'),
    getConcessionId: () => getValues('actionConcession'),
    onApply: (match: PkCandidate) => {
      // El mismo PK en los dos campos: la actuación es puntual.
      const pk = pkFormatter(match.pkKm, match.pkMeter, true);
      setValue('actionPkInit', pk);
      setValue('actionPkEnd', pk);

      if (match.roadId) {
        // El desplegable no lee el valor del formulario: si no se le dice,
        // sigue enseñando la vía anterior.
        roadDropdownInputRef.current?.setSelectedOption({
          label: match.roadName,
          value: match.roadId,
        });
        setValue('actionRoad', match.roadId);
      }
      if (match.roadRange) {
        setValue(
          'actionPkInitRoad',
          pkFormatter(match.roadRange.pkInitKm, match.roadRange.pkInitMeter, true)
        );
        setValue(
          'actionPkEndRoad',
          pkFormatter(match.roadRange.pkEndKm, match.roadRange.pkEndMeter, true)
        );
      }
    },
  });

  /**
   * La diana: centra el mapa y, con la MISMA posición, saca la vía y el PK.
   *
   * 🔴 Por `requestPositionOnce` y no por `navigator.geolocation` a pelo: la
   * llamada directa iba SIN `timeout` —infinito, por especificación— y podía no
   * llamar a ninguno de sus dos callbacks. El operario pulsaba y no pasaba
   * absolutamente nada: ni PK, ni error, ni marcador.
   *
   * Y el fallo ahora se TUESTA. Antes iba a `console.error`, que en una tablet
   * no lo lee nadie.
   */
  const handleGetLocation = useCallback(async () => {
    setIsLocating(true);
    try {
      const position = await requestPositionOnce();
      const { latitude, longitude } = position.coords;

      setCoordinates(() => {
        const coordinatesUpdated = { lat: latitude, lng: longitude };
        setValue('actionCoordinates', JSON.stringify(coordinatesUpdated));

        return coordinatesUpdated;
      });
      setCenterCoordinates({ lat: latitude, lng: longitude });

      // Y con la MISMA posición —la que se acaba de leer—, la vía y el PK.
      // Pasársela importa: si no, el hook pediría otra lectura de GPS y el
      // marcador y el PK saldrían de fixes distintos, que pueden diferir
      // varios metros. Va detrás del marcador a propósito: si la resolución
      // falla, el mapa ya está centrado.
      await autolocate.run(position);
    } catch (err) {
      // `code === 1` es PERMISSION_DENIED, la única negativa real del usuario.
      const code = (err as { code?: number } | undefined)?.code;
      showErrorToast(
        t(
          code === 1
            ? 'workOrder.autolocate.denied'
            : 'workOrder.autolocate.unavailable'
        )
      );
    } finally {
      setIsLocating(false);
    }
  }, [autolocate.run, setValue, t]);

  // C-11.13: al editar una action que ya tiene geoposición, parsear las
  // coords del form value (cargadas desde BD vía selectLocationValues) y
  // poner el marcador en el mapa. ANTES este useEffect pedía la posición
  // ACTUAL del navegador y la sobrescribía sobre `actionCoordinates`,
  // perdiendo las coords guardadas y mostrando un marcador en la posición
  // del usuario en lugar de la geoposición de la action.
  useEffect(() => {
    if (!actionCoordinates) return;
    try {
      const parsed = JSON.parse(actionCoordinates);
      if (
        typeof parsed?.lat === 'number' &&
        typeof parsed?.lng === 'number'
      ) {
        setCoordinates({ lat: parsed.lat, lng: parsed.lng });
        setCenterCoordinates({ lat: parsed.lat, lng: parsed.lng });
      }
    } catch {
      // valor corrupto en el form — ignorar, el botón seguirá disponible
    }
    // Solo on mount — los setters de useState son estables y no necesitan deps.
  }, []);

  return (
    <MainContainer>
      {centerCoordinates && displayMap && (
        <MapContainer>
          <MemorizedMap
            center={centerCoordinates}
            zoom={15}
            path={path}
            point={point}
            geoloc={coordinates}
            apiKey={import.meta.env.VITE_MAP_API_KEY}
          />
        </MapContainer>
      )}
      {/* INTEGRACIÓN 9: Eliminado onSubmit={handleSubmit} para prevenir bloqueo. Se usa botón manual. */}
      <GeneralContainer>
        <Title component="h3" variant="semiBold" size="L">
          {t('location.title')}
        </Title>
        <RowsContainer>
          <RowStyled>
            <Controller
              control={control}
              name="actionConcession"
              render={({
                field: { onChange, value },
                formState: { defaultValues },
              }) => {
                const options =
                  defaultValues?.concessions?.reduce((acc, curr) => {
                    if (curr?.name !== 'CX') {
                      acc.push({
                        label: curr?.name || '',
                        value: curr?.id || '',
                      });
                    }

                    return acc;
                  }, [] as GroupTypes[]) || [];

                // Si solo hay una concesión, auto-seleccionar y no mostrar nada
                if (options.length === 1) {
                  const concessionId = options[0].value;
                  if (value !== concessionId) {
                    onChange(concessionId);
                  }
                  return <></>;
                }

                // Si hay múltiples concesiones, mostrar dropdown
                return (
                  <DropDownInput
                    labelText={t('user.concession')}
                    placeholder={t('location.selectPlaceholder')}
                    inputSize={'XS'}
                    options={options}
                    onChangeSelected={(option) =>
                      onSelectConcession(option, onChange)
                    }
                    typeDropDown={'Default'}
                    defaultValue={defaultValues?.actionConcession}
                    borderError={!!errors.actionConcession}
                    error={errors.actionConcession?.message ? t(errors.actionConcession.message) : undefined}
                    isDisabled={
                      initialData?.parentExpeditionOrderStatus ===
                      OrderState.Annulled ||
                      initialData?.parentExpeditionOrderStatus ===
                      OrderState.End ||
                      initialData?.state === OrderState.Annulled
                    }
                  />
                );
              }}
            />
            <InputText
              {...register('actionCity')}
              typeInput={'normal'}
              disabled
              label={t('workOrder.location')}
              borderError={!!errors.actionCity}
              error={errors.actionCity?.message ? t(errors.actionCity.message) : undefined}
            />

            <RoadInputsGroup>
              <Controller
                name="actionRoad"
                control={control}
                render={({
                  field: { onChange },
                  formState: { defaultValues },
                }) => {
                  const options =
                    roads?.map((road) => ({
                      label: road?.name || '',
                      value: road?.id || '',
                    })) || [];
                  return (
                    <DropDownInput
                      labelText={t('workOrder.road')}
                      placeholder={t('location.selectPlaceholder')}
                      inputSize={'XS'}
                      options={options}
                      onChangeSelected={(option) =>
                        onSelectRoad(option, onChange)
                      }
                      typeDropDown={'Default'}
                      defaultValue={defaultValues?.actionRoad}
                      ref={roadDropdownInputRef}
                      borderError={!!errors.actionRoad}
                      error={errors.actionRoad?.message ? t(errors.actionRoad.message) : undefined}
                      isDisabled={
                        initialData?.parentExpeditionOrderStatus ===
                        OrderState.Annulled ||
                        initialData?.parentExpeditionOrderStatus ===
                        OrderState.End ||
                        initialData?.state === OrderState.Annulled
                      }
                    />
                  );
                }}
              />
              <InputText
                {...register('actionPkInitRoad')}
                typeInput={'normal'}
                disabled
                placeholder="000 + 000"
                label={t('workOrder.start')}
                inputSize="S"
                borderError={!!errors.actionPkInitRoad}
                error={errors.actionPkInitRoad?.message ? t(errors.actionPkInitRoad.message) : undefined}
              />
              <InputText
                {...register('actionPkEndRoad')}
                typeInput={'normal'}
                disabled
                placeholder="000 + 000"
                label={t('workOrder.end')}
                inputSize="S"
                borderError={!!errors.actionPkEndRoad}
                error={errors.actionPkEndRoad?.message ? t(errors.actionPkEndRoad.message) : undefined}
              />
            </RoadInputsGroup>
          </RowStyled>
          <RowStyled>
            <InputText
              {...registerPk(register, setValue, 'actionPkInit')}
              typeInput={'normal'}
              label={t('workOrder.pkInitial')}
              placeholder={t('common:pkExample')}
              inputSize="S"
              borderError={!!errors.actionPkInit}
              error={errors.actionPkInit?.message ? t(errors.actionPkInit.message) : undefined}
              disabled={
                initialData?.parentExpeditionOrderStatus ===
                OrderState.Annulled ||
                initialData?.parentExpeditionOrderStatus === OrderState.End ||
                initialData?.state === OrderState.Annulled
              }
            />
            <InputText
              {...registerPk(register, setValue, 'actionPkEnd')}
              typeInput={'normal'}
              label={t('workOrder.pkFinal')}
              placeholder={t('common:pkExample')}
              inputSize="S"
              borderError={!!errors.actionPkEnd}
              error={errors.actionPkEnd?.message ? t(errors.actionPkEnd.message) : undefined}
              disabled={
                initialData?.parentExpeditionOrderStatus ===
                OrderState.Annulled ||
                initialData?.parentExpeditionOrderStatus === OrderState.End ||
                initialData?.state === OrderState.Annulled
              }
            />
            <Controller
              name="actionTrack"
              control={control}
              render={({
                field: { onChange },
                formState: { defaultValues },
              }) => {
                const options = translateTrackOptions(
                  defaultValues?.groupTracks as CatalogEntry[] | undefined,
                  t
                );

                return (
                  <SearchableDropdown
                    labelText={t('workOrder.roadType')}
                    placeholder={t('location.selectPlaceholder')}
                    inputSize={'M'}
                    options={options}
                    typeDropDown={'Default'}
                    onChangeSelected={(option) => onChange(option?.value ?? '')}
                    defaultValue={defaultValues?.actionTrack}
                    borderError={!!errors.actionTrack}
                    error={errors.actionTrack?.message ? t(errors.actionTrack.message) : undefined}
                    isDisabled={
                      initialData?.parentExpeditionOrderStatus ===
                      OrderState.Annulled ||
                      initialData?.parentExpeditionOrderStatus ===
                      OrderState.End ||
                      initialData?.state === OrderState.Annulled
                    }
                  />
                );
              }}
            />
          </RowStyled>
          <RowStyled>
            <Controller
              control={control}
              name="actionDirection"
              render={({
                field: { onChange },
                formState: { defaultValues },
              }) => {
                const defaultStatusValue: PartialDirection = {
                  [ActionDirection.DOWN]: 'down',
                  [ActionDirection.TOP]: 'top',
                  [ActionDirection.BOTH]: 'both',
                  [ActionDirection.UNSPECIFIC]: UNSPECIFIC,
                };

                const directionOptions: PartialDirection = {
                  down: ActionDirection.DOWN,
                  top: ActionDirection.TOP,
                  both: ActionDirection.BOTH,
                  [UNSPECIFIC]: UNSPECIFIC,
                };
                return (
                  <ArrowRadioGroup
                    directions={['down', 'both', 'top', UNSPECIFIC]}
                    hasUnspecific
                    name="direction"
                    label={t('location.direction')}
                    defaultValue={
                      defaultStatusValue[
                      defaultValues?.actionDirection ||
                        defaultValues?.actionDirection === UNSPECIFIC
                        ? (defaultValues?.actionDirection as ActionDirection)
                        : (defaultValues?.direction as ActionDirection)
                      ]
                    }
                    onChangeDirection={(dir) => {
                      dir ? onChange(directionOptions[dir]) : onChange('');
                    }}
                    borderError={!!errors.actionDirection}
                    disabled={
                      initialData?.parentExpeditionOrderStatus ===
                      OrderState.Annulled ||
                      initialData?.parentExpeditionOrderStatus ===
                      OrderState.End ||
                      initialData?.state === OrderState.Annulled
                    }
                    directionLabels={{
                      down: actionRoadDirections?.[0],
                      top: actionRoadDirections?.[
                        actionRoadDirections?.length - 1
                      ],
                    }}
                  />
                );
              }}
            />

            {showMargin && (
              <Controller
                control={control}
                name="actionMargin"
                render={({
                  field: { onChange },
                  formState: { defaultValues },
                }) => {
                  const defaultStatusValue: PartialDirection = {
                    [ActionMargin.BOTH]: 'both',
                    [ActionMargin.LEFT]: 'left',
                    [ActionMargin.RIGHT]: 'right',
                    [ActionMargin.UNSPECIFIC]: UNSPECIFIC,
                  };

                  const options: PartialDirection = {
                    both: ActionMargin.BOTH,
                    left: ActionMargin.LEFT,
                    right: ActionMargin.RIGHT,
                    [UNSPECIFIC]: ActionMargin.UNSPECIFIC,
                  };

                  return (
                    <ArrowRadioGroup
                      directions={['left', 'both', 'right', UNSPECIFIC]}
                      hasUnspecific
                      name="margin"
                      label={t('location.margin')}
                      defaultValue={
                        defaultStatusValue[
                        defaultValues?.actionMargin ||
                          defaultValues?.actionMargin === UNSPECIFIC
                          ? (defaultValues?.actionMargin as ActionMargin)
                          : (defaultValues?.margin as ActionMargin)
                        ]
                      }
                      onChangeDirection={(dir) => {
                        dir ? onChange(options[dir]) : onChange('');
                      }}
                      borderError={!!errors.actionMargin}
                      disabled={
                        initialData?.parentExpeditionOrderStatus ===
                        OrderState.Annulled ||
                        initialData?.parentExpeditionOrderStatus ===
                        OrderState.End ||
                        initialData?.state === OrderState.Annulled
                      }
                    />
                  );
                }}
              />
            )}
            <Controller
              name="actionLane"
              control={control}
              render={({
                field: { onChange },
                formState: { defaultValues },
              }) => {
                const options = translateLaneOptions(
                  defaultValues?.groupLanes as CatalogEntry[] | undefined,
                  t
                );
                return (
                  <SearchableDropdown
                    labelText={t('workOrder.lane')}
                    placeholder={t('location.selectPlaceholder')}
                    inputSize={'S'}
                    options={options}
                    typeDropDown={'Default'}
                    onChangeSelected={(option) => onChange(option?.value ?? '')}
                    defaultValue={defaultValues?.actionLane}
                    borderError={!!errors.actionLane}
                    isDisabled={
                      initialData?.parentExpeditionOrderStatus ===
                      OrderState.Annulled ||
                      initialData?.parentExpeditionOrderStatus ===
                      OrderState.End ||
                      initialData?.state === OrderState.Annulled
                    }
                  />
                );
              }}
            />
            {actionCoordinates ? (
              <GreenButton
                // Botón de icono: sin texto no tiene nombre accesible, así que
                // no hay forma de seleccionarlo en un test sin esto.
                data-testid="geolocate-button"
                type={'button'}
                variant="diana"
                onClick={handleGetLocation}
                disabled={
                  // Mientras el GPS lee o el PK se resuelve: si no, el guard
                  // del hook se traga las pulsaciones sin decir nada.
                  isLocating ||
                  autolocate.isLoading ||
                  initialData?.parentExpeditionOrderStatus ===
                  OrderState.Annulled ||
                  initialData?.parentExpeditionOrderStatus === OrderState.End ||
                  initialData?.state === OrderState.Annulled
                }
              />
            ) : (
              <BlackButton
                data-testid="geolocate-button"
                type={'button'}
                variant="diana"
                onClick={handleGetLocation}
                disabled={
                  // Mientras el GPS lee o el PK se resuelve: si no, el guard
                  // del hook se traga las pulsaciones sin decir nada.
                  isLocating ||
                  autolocate.isLoading ||
                  initialData?.parentExpeditionOrderStatus ===
                  OrderState.Annulled ||
                  initialData?.parentExpeditionOrderStatus === OrderState.End ||
                  initialData?.state === OrderState.Annulled
                }
              />
            )}
          </RowStyled>
        </RowsContainer>
        {mapError && (
          <ContainerError>
            <Typography size="XS" colorText="lightCoralRed">
              {mapError}
            </Typography>
          </ContainerError>
        )}
        <ButtonContainer>
          <MainButton
            text={textButton}
            type="button" // ✅ Botón manual, no submit
            onClick={handleSaveDraft} // ✅ Guardar draft y navegar sin validar
            disabled={
              initialData?.parentExpeditionOrderStatus === OrderState.Annulled ||
              initialData?.parentExpeditionOrderStatus === OrderState.End ||
              initialData?.state === OrderState.Annulled
            }
          />
        </ButtonContainer>
      </GeneralContainer>
    </MainContainer>
  );
};

export default LocationForm;
